<script setup lang="ts">
import VueMarkdown from 'vue-markdown-render'
import { toast, type ToastOptions } from 'vue3-toastify'
import { useI18n } from 'vue-i18n'
import KitsShowcaseWithFilters from '~/components/kits/KitsShowcaseWithFilters.vue'
import { useStrapiKitsListPageWithNoLocaleFilter } from '~/composables/useStrapiKits'
import { useTransactionalPageResource } from '~/composables/transactionalPage/useTransactionalPageResource'
import { useTransactionalProductSchema } from '~/composables/schemaOrg/useTransactionalProductSchema'
import useTransactionalPageHead from '~/composables/transactionalPage/useTransactionalPageHead'
import { EventListPageType } from '~/composables/types/strapiEventListPage'
import { useStrapiKitsListSummaries } from '~/composables/useStrapiKitsListSummaries'
import {
  KITS_BROWSE_PRODUCTS_KEY,
  useKitsBrowseProducts,
} from '~/composables/useKitsBrowseProducts'
import { markdownItScrollableTables } from '~/utils/markdownItScrollableTables'
import { useDynamicContentMetrics } from '~/composables/useDynamicContentMetrics'
import { dynamicContentTransformer } from '~/utils/dynamicContentTransformer'
import type { KftDynamicContentMetrics } from '~/utils/kftApiUtils'

const route = useRoute()
const { t, locale } = useI18n()
const boxSlug = route.params.category?.toString() || ''

const strapiKitsListPageWithNoLocaleFilter =
  await useStrapiKitsListPageWithNoLocaleFilter({
    slug: boxSlug,
  })

const transactionalPageResource = await useTransactionalPageResource({
  strapiEventListPageWithNoLocaleFilter: strapiKitsListPageWithNoLocaleFilter,
})

const kitsObject = computed(() => {
  const entry =
    transactionalPageResource?.data?.value?.find(
      (entry) => entry?.locale.toLowerCase() === locale.value
    ) || transactionalPageResource?.data?.value?.[0]

  if (!entry) return null

  const result =
    strapiEventListPageGetters.getLocalizationByLocale(entry, locale.value) ||
    entry

  return result
})

if (strapiKitsListPageWithNoLocaleFilter.error.value) {
  toast(t('general.error'), {
    autoClose: 2000,
    type: 'error',
    position: toast.POSITION.TOP_CENTER,
    multiple: false,
  } as ToastOptions)
}

if (!kitsObject.value) {
  throw showError({ statusCode: 404, statusMessage: 'Page Not Found' })
}

const { fetchDynamicContentMetrics } = useDynamicContentMetrics()
const metricsCategorySlug = kitsObject.value.categorySlug
const nuxt = useNuxtApp()

await useAsyncData(
  `kits-metrics-${metricsCategorySlug}`,
  async () => {
    const metrics = metricsCategorySlug
      ? await fetchDynamicContentMetrics('Category', metricsCategorySlug)
      : null

    const metricsWithCurrentYear: KftDynamicContentMetrics = {
      ...(metrics ?? ({} as KftDynamicContentMetrics)),
      currentYear: new Date().getFullYear(),
    }

    if (kitsObject.value) {
      dynamicContentTransformer(
        kitsObject.value,
        metricsWithCurrentYear,
        locale.value
      )
    }

    return metrics
  },
  {
    server: true,
    lazy: false,
    dedupe: 'defer',
    getCachedData(key) {
      return nuxt.isHydrating ? nuxt.payload.data[key] : nuxt.static.data[key]
    },
  }
)

const { data: kitsSummaries } = await useStrapiKitsListSummaries()

const sortedKitLinks = computed(() => {
  const list = kitsSummaries.value ?? []
  return [...list].sort((a, b) => a.id - b.id)
})

// Algolia facet uses Strapi `categorySlug` (e.g. malkurs), not the URL segment (e.g. malset).
const browse = useKitsBrowseProducts({
  initialCategorySlug: kitsObject.value?.categorySlug ?? null,
})
provide(KITS_BROWSE_PRODUCTS_KEY, browse)

watch(
  () => kitsObject.value?.categorySlug,
  (slug) => {
    browse.categorySlug.value = slug ?? null
  },
  { immediate: true }
)

const cardShowcase = computed(() => {
  const ko = kitsObject.value
  return Object.keys(ko?.showcase || {})
    ?.map((key) => ko?.showcase[key])
    .filter((cardDetails) => cardDetails?.id)
    .map((cd) => ({
      ...cd,
      imageUrl: strapiImageGetters.getImageUrl(cd.image),
    }))
})

const products = computed(() => browse.products.value)

const searchHeading = computed(
  () => kitsObject.value?.searchHeading || kitsObject.value?.title || ''
)

const eventCount = computed(() => browse.totalHits.value)

const { shouldAppendTransactionalProductSchema, schemaObj } =
  await useTransactionalProductSchema({
    locationSlug: computed(() => undefined),
    tagSlug: computed(() => boxSlug),
    categorySlug: computed(() => kitsObject.value?.categorySlug),
    eventListPageData: kitsObject,
    pageType: EventListPageType.KITS,
    searchHeading,
    hits: products,
    eventCount,
  })

useTransactionalPageHead(
  EventListPageType.KITS,
  kitsObject.value,
  {
    // The kits ItemList comes from KitsShowcaseWithFilters.
    script: [
      ...(shouldAppendTransactionalProductSchema.value && eventCount.value
        ? [schemaObj]
        : []),
    ],
  },
  {
    hits: products,
    shouldBeIndexed: true,
  }
)
</script>

<template>
  <div>
    <TransactionalPageBreadcrumbs
      class="my-4"
      :type="EventListPageType.KITS"
      :event-list-page-obj="kitsObject"
    />

    <div class="event-list-page__section">
      <div
        class="relative h-[80px] md:h-auto rounded-xl overflow-hidden md:overflow-visible flex items-center"
      >
        <NuxtImg
          preload
          class="card-image object-cover w-full md:hidden"
          src="/_nuxt3-static/general/transactional-pages-banner.webp"
          alt="Ein Barkeeper gießt Cocktails in zwei Gläser."
          loading="eager"
          fetchpriority="high"
        />

        <div
          class="w-full h-full bg-blend-hard-light bg-linear-to-r from-white to-transparent absolute"
        />

        <Heading
          v-if="kitsObject.searchHeading || kitsObject.title"
          class="absolute md:relative"
          h-class="text-h5 text-teal-900!"
          :title="kitsObject.searchHeading || kitsObject.title"
          :level="1"
        />
      </div>
    </div>

    <KitsShowcaseWithFilters :categories="sortedKitLinks" />

    <NuxtLazyHydrate when-visible>
      <TransactionalPageCardListFromStrapi
        v-if="
          strapiEventListPageGetters.getHorizontalCardList(kitsObject).length
        "
        :event-list-page="kitsObject"
        class="pt-10"
        data-test-id="horizontal-card-list"
      />
    </NuxtLazyHydrate>

    <NuxtLazyHydrate v-if="kitsObject?.description" when-visible>
      <vue-markdown
        class="prose my-5 w-full max-w-full min-w-0"
        :source="kitsObject?.description || ''"
        :plugins="[markdownItScrollableTables]"
      />
    </NuxtLazyHydrate>

    <div class="mt-10">
      <Heading
        :level="2"
        :title="strapiEventListPageGetters.getShowcaseTitle(kitsObject)"
        class="kft-heading--left mb-10"
      />
      <NuxtLazyHydrate when-visible>
        <SimpleCardShowcase
          :showcase-items="cardShowcase"
          data-test-id="simple-showcase"
        />
      </NuxtLazyHydrate>
    </div>
  </div>
</template>
